// frontend/src/hooks/useNetsuitePractitionerNames.js
/*
 * Resolves human-readable NetSuite names for the five NetSuite IDs stored on
 * a practitioner profile (vendor + revenue/af expense/lab expense/invisalign
 * accounts). Used by the practitioner quick-look modal and create form so
 * admins can see "Name (#ID)" instead of just a bare ID.
 *
 * Inputs are id-like values (string/number/empty). Empty / non-positive ids
 * are ignored. Vendor lookup hits /api/netsuite/vendors/, account ids are
 * batched into a single /api/netsuite/accounts/ request.
 */

import { useEffect, useMemo, useState } from "react";
import { useNetsuiteAccounts, useNetsuiteVendors } from "@api/hooks";

const ACCOUNT_KEYS = [
  "netsuite_revenue_id",
  "netsuite_af_expense_id",
  "netsuite_lab_expense_id",
  "netsuite_invisalign_id",
];

// Wait this long after the last keystroke before hitting NetSuite, so each
// digit typed doesn't trigger its own request.
const LOOKUP_DEBOUNCE_MS = 400;

function normalizeId(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (s === "") return "";
  if (!/^\d+$/.test(s)) return "";
  return s;
}

export function formatNetsuiteName(name, id) {
  const cleanName = (name || "").trim();
  const cleanId = normalizeId(id);
  if (cleanName && cleanId) return `${cleanName} (#${cleanId})`;
  if (cleanName) return cleanName;
  if (cleanId) return `#${cleanId}`;
  return "";
}

/**
 * @param {object} source - object/form with the five netsuite_*_id keys.
 */
export function useNetsuitePractitionerNames(source) {
  const liveVendorId = normalizeId(source?.netsuite_vendor_id);

  const liveAccountIds = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const key of ACCOUNT_KEYS) {
      const id = normalizeId(source?.[key]);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
    // We deliberately depend on each individual ID field (which is what we
    // actually read) instead of the entire `source` object — the form object
    // changes identity on every keystroke, which would defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    source?.netsuite_revenue_id,
    source?.netsuite_af_expense_id,
    source?.netsuite_lab_expense_id,
    source?.netsuite_invisalign_id,
  ]);

  // Debounce the IDs we feed to react-query so we only refetch after the
  // user stops typing. Stable string key avoids resetting the timer when
  // array identity changes but contents don't.
  const liveAccountKey = liveAccountIds.join(",");
  const [debouncedVendorId, setDebouncedVendorId] = useState(liveVendorId);
  const [debouncedAccountKey, setDebouncedAccountKey] = useState(liveAccountKey);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedVendorId(liveVendorId);
      setDebouncedAccountKey(liveAccountKey);
    }, LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [liveVendorId, liveAccountKey]);

  const debouncedAccountIds = useMemo(
    () => (debouncedAccountKey ? debouncedAccountKey.split(",") : []),
    [debouncedAccountKey],
  );

  const vendorQuery = useNetsuiteVendors(
    { id: debouncedVendorId },
    { enabled: !!debouncedVendorId },
  );
  const accountsQuery = useNetsuiteAccounts(
    { ids: debouncedAccountIds },
    { enabled: debouncedAccountIds.length > 0 },
  );

  const vendorName = useMemo(() => {
    if (!debouncedVendorId) return "";
    const data = vendorQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const match = list.find((v) => String(v?.id ?? v?.ID) === debouncedVendorId);
    return match?.fullname ?? match?.entitytitle ?? "";
  }, [vendorQuery.data, debouncedVendorId]);

  const accountNameById = useMemo(() => {
    const data = accountsQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const map = {};
    for (const a of list) {
      const id = a?.id ?? a?.ID;
      const name = a?.displaynamewithhierarchy ?? a?.displayNameWithHierarchy;
      if (id !== undefined && id !== null) map[String(id)] = name ?? "";
    }
    return map;
  }, [accountsQuery.data]);

  // While the user is still typing (live id != debounced id), or the query
  // is in flight, treat the value as "loading" so the UI shows a spinner
  // instead of "no match found".
  const vendorPending =
    !!liveVendorId && (liveVendorId !== debouncedVendorId || vendorQuery.isFetching);
  const accountsPending =
    liveAccountKey !== "" && (liveAccountKey !== debouncedAccountKey || accountsQuery.isFetching);

  function getDisplayFor(fieldKey, idValue) {
    const id = normalizeId(idValue);
    if (!id) return "";
    if (fieldKey === "netsuite_vendor_id") {
      return formatNetsuiteName(vendorName, id);
    }
    return formatNetsuiteName(accountNameById[id], id);
  }

  function getName(fieldKey, idValue) {
    const id = normalizeId(idValue);
    if (!id) return "";
    if (fieldKey === "netsuite_vendor_id") return vendorName;
    return accountNameById[id] || "";
  }

  function isPendingFor(fieldKey, idValue) {
    if (!normalizeId(idValue)) return false;
    if (fieldKey === "netsuite_vendor_id") return vendorPending;
    return accountsPending;
  }

  return {
    vendorName,
    accountNameById,
    isLoading: vendorQuery.isLoading || accountsQuery.isLoading,
    isPendingFor,
    getDisplayFor,
    getName,
  };
}

export default useNetsuitePractitionerNames;
